import { useState } from 'react'
import { fetcher, AdminSeasonChallengeTasksEndpoints, IUseSeasonChallengeTasks, AdminQuestEndpoints, AdminTournamentsEndpoints, AdminSurveysEndpoints, AdminGameEndpoints, AdminTokensEndpoints, AdminEventsEndpoints, fetcherNewsRoom } from '@olagg/api-hooks'
import { AdminSeasonChallengeTask } from '@olagg/db-types'
import { IUseQuests } from '@olagg/api-hooks/interfaces/IUseQuests'
import { IUseTournaments } from '@olagg/api-hooks/interfaces/IUseTournaments'
import { IUseTokens } from '@olagg/api-hooks/interfaces/IUseTokens'

interface SeasonChallengeTasksResponse {
  gameSeasonChallengeTasks: AdminSeasonChallengeTask[]
  meta: {
    count: number
  }
}

const useSeasonChallengeTasks = () => {
  const [loading, setLoading] = useState(false)

  const getActiveQuests = async (filter: IUseQuests) => {
    try {
      setLoading(true)
      const endpoint = AdminQuestEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response.quests || []
    } catch (error) {
      console.error('Error fetching active quests:', error)
      return []
    } finally {
      setLoading(false)
    }
  }

  const getTournaments = async (filter: IUseTournaments) => {
    try {
      setLoading(true)
      const endpoint = AdminTournamentsEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response.tournaments || []
    } catch (error) {
      console.error('Error fetching tournaments:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getSurveys = async (filter: IUseQuests) => {
    try {
      setLoading(true)
      const endpoint = AdminSurveysEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response.surveys || []
    } catch (error) {
      console.error('Error fetching surveys:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getGames = async (filter: IUseQuests) => {
    try {
      setLoading(true)
      const endpoint = AdminGameEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response.games || []
    } catch (error) {
      console.error('Error fetching games:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getTokens = async (filter: IUseTokens) => {
    try {
      setLoading(true)
      const endpoint = AdminTokensEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response.tokens || []
    } catch (error) {
      console.error('Error fetching tokens:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getEvents = async (filter: { page: number; limit: number; title?: string }) => {
    try {
      setLoading(true)
      const endpoint = AdminEventsEndpoints.all({...filter, active: true})
      const response = await fetcher(endpoint)
      return response.events || []
    } catch (error) {
      console.error('Error fetching events:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getSeasonChallengeTasks = async (filter: IUseSeasonChallengeTasks): Promise<SeasonChallengeTasksResponse> => {
    try {
      setLoading(true)
      const endpoint = AdminSeasonChallengeTasksEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error fetching tasks:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getSeasonChallengeTask = async (id: string): Promise<AdminSeasonChallengeTask> => {
    try {
      setLoading(true)
      const endpoint = AdminSeasonChallengeTasksEndpoints.find(id)
      const response = await fetcher(endpoint)
      return response.gameSeasonChallengeTask || response
    } catch (error) {
      console.error('Error fetching task:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createSeasonChallengeTask = async (data: Partial<AdminSeasonChallengeTask>): Promise<AdminSeasonChallengeTask> => {
    try {
      setLoading(true)
      const endpoint = AdminSeasonChallengeTasksEndpoints.create(data)
      const response = await fetcher(endpoint)
      return response.gameSeasonChallengeTask || response
    } catch (error) {
      console.error('Error creating task:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateSeasonChallengeTask = async (id: string, data: Partial<AdminSeasonChallengeTask>): Promise<AdminSeasonChallengeTask> => {
    try {
      setLoading(true)
      const endpoint = AdminSeasonChallengeTasksEndpoints.update(id, data)
      const response = await fetcher(endpoint)
      return response.gameSeasonChallengeTask || response
    } catch (error) {
      console.error('Error updating task:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteSeasonChallengeTask = async (id: string): Promise<void> => {
    try {
      setLoading(true)
      const endpoint = AdminSeasonChallengeTasksEndpoints.delete(id)
      await fetcher(endpoint)
    } catch (error) {
      console.error('Error deleting task:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    getSeasonChallengeTasks,
    getSeasonChallengeTask,
    createSeasonChallengeTask,
    updateSeasonChallengeTask,
    deleteSeasonChallengeTask,
    getActiveQuests,
    getTournaments,
    getSurveys,
    getGames,
    getTokens,
    getEvents,
  }
}

export default useSeasonChallengeTasks;
